import { useContext } from 'react';
import LsqActionMenu from '../components/LsqActionMenu';
import { WidgetBuilderContext } from '../contexts/WidgetBuilderContext';
import { usePreviewPaneContext } from '../contexts/PreviewPaneContext';
import { useWidgetMutations } from '../DashboardSettingsScreen/hooks/useWidgetMutations';
import { useSectionMutations } from '../views/PreviewPane/hooks/useSectionMutations';
import useToast from '../hooks/useToast';
import { ReactComponent as LSQMore } from '../assets/svgIcons/lsq_more.svg';
import './style.css';
import { getWidgetTitle, hasConfigurationScreen } from '../utils/widgetUtils';
import cloneDeep from 'lodash.clonedeep';
import { topFixedWidgets } from '../constants/globalConstant';
import { useBuilderDataContext } from '../hooks/BuilderDataContext';

export const WidgetMenu = ({ children, styles, widgetInformation }) => {
  const toast = useToast();
  const builderData = useBuilderDataContext();
  const homepageId = builderData?.configId;
  const widgetBuilder = useContext(WidgetBuilderContext);
  const mode = widgetBuilder?.selectedWidget?.mode ?? null;
  const {
    previewPaneData: previewPaneState,
    updatePreviewPane,
    setPreviewPaneDirty
  } = usePreviewPaneContext();
  const { sections } = previewPaneState;
  const { deleteWidget: deleteWidgetMutation } = useWidgetMutations(homepageId, previewPaneState);
  const { deleteSection: deleteSectionMutation, updateLayout: updateLayoutMutation } =
    useSectionMutations(homepageId, previewPaneState);
  const widgetTemplateId = widgetInformation?.widgetTemplateId;
  let widgetTitle = getWidgetTitle(widgetTemplateId);

  const deleteTopFixedWidget = async () => {
    if (sections.length === 1) {
      toast.warn('You cannot delete all of the sections from the layout!');
      return;
    }

    const rowsCountInTopFixedSections = sections[0]?.rows.length;

    // If only one widget present in top-fixed section, then delete section
    if (rowsCountInTopFixedSections === 1) {
      await deleteSectionMutation({ sectionIndex: 0, shouldUpdateLayout: true });
      const _sections = cloneDeep(sections);
      _sections.shift();
      updatePreviewPane({ sections: _sections, selectedSection: 0 });
      setPreviewPaneDirty(true);
      updateLayoutMutation({ sections: _sections, isPreviewPaneDirty: true })
        .then(() => setPreviewPaneDirty(false))
        .catch(() => {});
    } else {
      await deleteWidgetMutation({
        widgetId: widgetInformation.widgetId,
        shouldUpdateLayout: false
      });
    }
  };

  const handleSelect = (item) => {
    if (item.value === 'edit') {
      widgetBuilder?.changeSelectedWidget({
        mode: 'UPDATE',
        widgetTitle: widgetTitle,
        widgetDetails: widgetInformation
      });
    } else if (item.value === 'delete') {
      if (topFixedWidgets.includes(widgetInformation.widgetTemplateId)) {
        deleteTopFixedWidget();
      } else {
        deleteWidgetMutation({
          widgetId: widgetInformation.widgetId,
          shouldUpdateLayout: false
        });
      }
    }
  };

  const actions = [
    ...(hasConfigurationScreen(widgetTemplateId) ? [{ label: 'Edit', value: 'edit' }] : []),
    { label: 'Delete', value: 'delete' }
  ];

  const triggerStyle = {
    ...styles,
    background: 'rgba(255, 255, 255, 0.75)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 2px',
    backdropFilter: 'blur(1px)'
  };

  return (
    <div className="relative" data-testid="lsq-menu">
      {mode !== null ? (
        <div
          className="absolute z-5"
          style={{ ...triggerStyle, cursor: 'not-allowed' }}
          data-testid="widget-more-icon">
          <LSQMore />
        </div>
      ) : (
        <LsqActionMenu actions={actions} onSelect={handleSelect}>
          <div
            className="absolute z-5"
            style={{ ...triggerStyle, cursor: 'pointer' }}
            data-testid="widget-more-icon">
            <LSQMore />
          </div>
        </LsqActionMenu>
      )}
      {children}
    </div>
  );
};
